import { createModule } from 'terrific-singleton';
import { TerrificSpec } from 'terrific-singleton/terrific-module';
import { triggerPublicEvent, PublicEvents } from '@project/shared/utils/events/js/events';
import { scrollToElement } from '@project/shared/utils/scroll-to/js/scroll-to';

const classes = {
	dependencyOptionValue: 'other',
	selectLink: '.js-m-language-switch__switch',
	accordionMoreBtnContainer: '.m-global-contact__more-accordion',
	accordionMoreBtn: '.m-global-contact__more-accordion .a-button',
	accordionMoreBtnActive: 'state-a-button--accordion-active',
	addressContainer: '.m-global-contact__contacts',
	addressElement: '.m-global-contact__contact',
	addressContainerShowLess: 'js-m-global-contact__contacts--show-less', 
	addressCollector: '.js-m-global-contact__contact-collector',
	preloader: '.m-global-contact__preloader .a-loading-indicator',
	wechatContainer: '.m-global-contact__wechat',
	wechatState: 'state-m-global-contact--cn',
	errorNoData: '.js-m-global-contact__error-handling-no-data',
	errorNoResponse: '.js-m-global-contact__error-handling-no-response',
};

@createModule('GlobalContact')
/**
 * GlobalContact module implementation.
 */
export class GlobalContact extends TerrificSpec {
	/* eslint-disable no-undef */
	$ctx: any;
	$accordionBtn: any;
	$languageSelect: any;
	$categorySelect: any;
	$wechatContainer: any;
	$header: any;
	baseUrl: string;
	accordionToggleTextOn: string;
	accordionToggleTextOff: string;
	showLoadMoreSpinner = true;
	numOfAddresses: number;
	requiredNumOfAddressesForScrolling: number;
	scrollOnCloseAccordion: boolean;
	/* eslint-enable no-undef */

	/**
	 * Start Lifecycle
	 * @param {Function} resolve Resolve
	 * @returns {void}
	 */
	start(resolve: Function) {
		this.$ctx = $(this._ctx);
		this.$wechatContainer = this.$ctx.find(classes.wechatContainer);
		this.$header = $('[data-t-name="header"]');
		this.setAccordionActions();
		this.setLanguageSelectActions();
		this.setAutoScrollActions();
		this.hideErrorMessages();
		resolve();
	}

	/**
	 * Start ajax call
	 * @param {string} lang lang
	 * @returns {void}
	 */
	responseRequest(lang: string) {
		const url = this.$languageSelect.attr('data-endpoint');

		this.initializeOnRequest();

		setTimeout(() => {
			$.ajax({
				type: 'GET',
				url: url + lang,
				contentType: 'application/json; charset=utf-8',
				async: true,
				dataType: 'json',
				success: (res: string[]) => {
					this.hidePreloader();

					if (res.length === 0) {
						// checked if address locations are available for this country
						this.noDataAvailable();
					} else {
						// otherwise there is data available
						this.responseSuccess(res, lang);
					}
				},
				error: () => {
					this.hidePreloader();
					this.noServerResponse();
				},
			});
		}, 250);
	}

	/**
	 * Initializes involved elemnts
	 * @returns {void}
	 */
	initializeOnRequest() {
		this.showPreloader();
		this.removeLocations();
		this.$accordionBtn.hide();
		this.$wechatContainer.hide();
		this.hideErrorMessages();
	}

	/**
	 * showPreloader
	 * @returns {void}
	 */
	showPreloader() {
		this.$ctx.find(classes.preloader).show();
	}

	/**
	 * hidePreloader
	 * @returns {void}
	 */
	hidePreloader() {
		this.$ctx.find(classes.preloader).hide();
	}

	/**
	 * Success ajax callback
	 * @param {Array} res res
	 * @param {string} lang language
	 * @returns {void}
	 */
	responseSuccess(res: any, lang: string) {
		this.hidePreloader();
		// eslint-disable-next-line prefer-template
		this.$ctx.find('.' + classes.addressContainerShowLess).show();
		this.addLocations(res);
		if (this.shouldShowAccordionBtn()) {
			this.$accordionBtn.show();
		}

		// Special case for china shows wechat qr code
		if (lang === 'CN') {
			this.$wechatContainer.show();
			this.$accordionBtn.closest(classes.accordionMoreBtnContainer).addClass(classes.wechatState);
		} else {
			this.$wechatContainer.hide();
			this.$accordionBtn.closest(classes.accordionMoreBtnContainer).removeClass(classes.wechatState);
		}
	}

	/**
	 * Remove MarkUp From Dom
	 * @returns {void}
	 */
	removeLocations() {
		this.$ctx.find(classes.addressElement).remove();
	}

	/**
	 * Add MarkUp To Dom
	 * @param {Object} res res
	 * @returns {void}
	 */
	addLocations(res: any) {
		/* eslint-disable */
		const $target = this.$ctx.find(classes.addressCollector);
		/* eslint-enable */
		$target.append(this.prepareLocationsMarkUp(res));

		this.numOfAddresses = $target.find(classes.addressElement).length;
	}

	/**
	 * Create MarkUp
	 * @param {Object} res response
	 * @returns {string} string
	 */
	prepareLocationsMarkUp(res: any): string {
		let str = '';
		res.forEach((d: string) => {
			/* eslint-disable */
			str +=
				'<div class="m-global-contact__contact"><div class="a-richtext a-richtext--copy">' + d + '</div></div>';
			/* eslint-enable */
		});
		return str;
	}

	/**
	 * Should show accordion btn
	 * @returns {boolean} boolean
	 */
	shouldShowAccordionBtn() {
		/* Disable linting to avoid warning for not allowed template concatenation rule */
		/* eslint-disable */
		const $contacts: any = this.$ctx.find(classes.addressContainer + ' ' + classes.addressElement);
		/* eslint-enable */
		return $contacts.length > 1;
	}

	/**
	 * setAccordionActions
	 * @returns {void}
	 */
	setAccordionActions() {
		this.$accordionBtn = this.getAccordionBtn();
		this.$accordionBtn.on('click', () => this.accordionToggleEvt());
		this.accordionSetToggleText();

		if (!this.shouldShowAccordionBtn()) {
			this.$accordionBtn.hide();
		}
	}

	/**
	 * setLanguageSelectActions
	 * @returns {void}
	 */
	setLanguageSelectActions() {
		this.$languageSelect = this.getSelect();
		this.baseUrl = this.$languageSelect.data('baseUrl');
		this.$languageSelect.on('change', (ev: Event) => this.selectedOptionChanged(ev));
		this.checkLocationParamsAndSet(this.$languageSelect);
	}


	/**
	 * Toggle Accordion Class to show or hide more locations
	 * @returns {void}
	 */
	accordionToggleEvt() {
		this.accordionToggleText();
		this.accordionClassToggle();

		/* Disable linting to avoid warning for .parent().parent() not allowed chaining rule */
		/* eslint-disable */
		const $accordionBtnContainer: any = this.$ctx
			.find(classes.accordionMoreBtn)
			.parent()
			.parent();
		/* eslint-enable */

		if ($accordionBtnContainer.hasClass(classes.addressContainerShowLess)) {
			$accordionBtnContainer.removeClass(classes.addressContainerShowLess);
		} else {
			$accordionBtnContainer.addClass(classes.addressContainerShowLess);
		}
	}

	/**
	 * Set accordion text for on and off state
	 * @returns {void}
	 */
	accordionSetToggleText() {
		this.accordionToggleTextOn = this.$ctx.find(classes.accordionMoreBtn).text();
		this.accordionToggleTextOff = this.$ctx.find(classes.accordionMoreBtn).data('text-toggle');
	}

	/**
	 * Accordion text toggle
	 * @returns {void}
	 */
	accordionToggleText() {
		const $accordionBtn = this.$ctx.find(classes.accordionMoreBtn);

		if ($accordionBtn.text() === this.accordionToggleTextOn) {
			// Show less text
			$accordionBtn.attr('data-text-toggle', this.accordionToggleTextOn);
			$accordionBtn.text(this.accordionToggleTextOff);
		} else {
			// Show more text
			$accordionBtn.attr('data-text-toggle', this.accordionToggleTextOff);
			$accordionBtn.text(this.accordionToggleTextOn);

			// Perform action only when closing accordion button
			if (this.shouldScrollToElement()) {
				scrollToElement(this.$ctx, true, this.$header.outerHeight());
			}
		}
	}

	/**
	 * Should scroll to global contact's top position on the page when more than given addresses in variable
	 * 'this.requiredNumOfAddressesForScrolling' has been shown after closing the accordion button.
	 * Avoids destroying focus and keeps user visually in track.
	 * @returns {boolean} Boolean value
	 */
	shouldScrollToElement(): boolean {
		return this.numOfAddresses > this.requiredNumOfAddressesForScrolling && this.scrollOnCloseAccordion;
	}

	/**
	 * Accordion class toggle
	 * @returns {void}
	 */
	accordionClassToggle() {
		const $accordionBtn = this.$ctx.find(classes.accordionMoreBtn);

		if ($accordionBtn.hasClass(classes.accordionMoreBtnActive)) {
			$accordionBtn.removeClass(classes.accordionMoreBtnActive);
		} else {
			$accordionBtn.addClass(classes.accordionMoreBtnActive);
		}
	}

	/**
	 * Extract parameter name from baseUrl
	 * @returns {void}
	 */
	getParamNameFromBaseUrl() {
		return this.baseUrl.substr(1, this.baseUrl.length - 2);
	}

	/**
	 * Check params for language in browser's location bar.
	 * See inputSelect.attr.value = "?SelectedCountry" in global-contact.json
	 *
	 * @param {jQuery} $select Select option (drop down)
	 * @returns {void}
	 */
	checkLocationParamsAndSet($select: any) {
		const str = document.location.search;
		const params = new Map(<any[]>str
			.slice(1)
			.split('&')
			.map((m) => m.split('=')));
		const param = this.getParamNameFromBaseUrl();

		if (params.has(param)) {
			$select.val(params.get(param));
		} else {
			$select.val('-1');
		}
	}

	/**
	 * follow link of selected option on change
	 *
	 * @param {Event} ev value of select option
	 * @returns {void}
	 */
	selectedOptionChanged(ev: any) {
		const lang = ev.currentTarget.value;
		this.responseRequest(lang);
	}
 
	/**
	 * Error handling no data available
	 * @returns {void}
	 */
	noDataAvailable() {
		const $target = this.$ctx.find(classes.errorNoData);
		$target.show();
		this.hideShowLessContainer();
	}

	/**
	 * Error handling no ajax request
	 * @returns {void}
	 */
	noServerResponse() {
		const $target = this.$ctx.find(classes.errorNoResponse);
		$target.show();
		this.hideShowLessContainer();
	}

	/**
	 * Hide all error messages
	 * @returns {void}
	 */
	hideErrorMessages() {
		const $targetNoData = this.$ctx.find(classes.errorNoData);
		const $targetNoResponse = this.$ctx.find(classes.errorNoResponse);

		// this.hideShowLessContainer();
		$targetNoData.hide();
		$targetNoResponse.hide();
	}

	/**
	 * hide additional address container
	 * @returns {void}
	 */
	hideShowLessContainer() {
		// eslint-disable-next-line prefer-template
		this.$ctx.find('.' + classes.addressContainerShowLess).hide();
	}

	/**
	 * Returns jQuery select option DOM element
	 * @returns {Object} jQuery object
	 */
	getSelect(): any {
		const str = classes.selectLink;
		return this.$ctx.find(str);
	}

	/**
	 * Returns jQuery select option DOM element
	 * @returns {Object} jQuery object
	 */
	getAccordionBtn(): any {
		const str = classes.accordionMoreBtn;
		return this.$ctx.find(str);
	}

	/**
	 * Start a scrolling to the top of the global contacts area
	 * @returns {void}
	 */
	setAutoScrollActions() {
		this.requiredNumOfAddressesForScrolling = 3;
		this.scrollOnCloseAccordion = true;
	}

	/**
	 * _triggerChangeEvent
	 * @returns {void}
	 */
	_triggerChangeEvent() {
		triggerPublicEvent(PublicEvents.uxChange, this, this._ctx);
	}

	/**
	 * @returns {void}
	 */
	stop() {}
}
